import { skipToken } from "@reduxjs/toolkit/query"
import clsx from "clsx"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { SpinnerCircular } from "spinners-react"
import { useGame } from "@/providers/GameProvider"
import type { IGameplayLog } from "@/store/apis/gameplayAPI"
import { useGetMatchLogsHistoryQuery } from "@/store/apis/matchAPI"
import { OutlinedText } from "@/ui/OutlinedText"
import { DEFAULT_PAGE_SIZE, Pagination } from "@/ui/Pagination"
import styles from "./MatchLogs.module.scss"
import type { IMatchLogs } from "./types"
import { LogsList } from "./ui/LogsList/LogsList"

const EMPTY_LOGS: readonly IGameplayLog[] = []

export function MatchLogs({
  className,
  isDesktop = false,
  ...props
}: IMatchLogs) {
  const { t } = useTranslation()
  const { sessionId, lastLogs, matchSession, isMatchActive } = useGame()

  const [page, setPage] = useState<number>(1)

  // Reset pagination whenever the viewed match changes — otherwise a stale
  // `page` from a previous (longer) match would produce a negative `turn` cursor
  // and a 400 from the logs endpoint.
  useEffect(() => {
    setPage(1)
  }, [sessionId])

  // History is paginated from the newest turn backwards.
  // turn=0 is a valid cursor (represents the initial game state),
  // so total items = totalTurns + 1.
  const totalTurns = matchSession?.snapshot?.state.turn ?? 0
  const totalItems = totalTurns + 1
  const turnCursor = totalTurns - (page - 1) * DEFAULT_PAGE_SIZE

  const { data: logsHistoryData, isFetching: isLogsHistoryLoading } =
    useGetMatchLogsHistoryQuery(
      !sessionId || isMatchActive
        ? skipToken
        : {
            sessionId,
            limit: DEFAULT_PAGE_SIZE,
            turn: turnCursor
          }
    )

  return (
    <div
      className={clsx(styles.MatchLogs, className, {
        [styles.desktop]: isDesktop
      })}
      {...props}
    >
      <div className={styles.MatchLogs_title}>
        <OutlinedText>{t("matchLogs")}</OutlinedText>
        {isDesktop && <span>{sessionId}</span>}
        {!isMatchActive && totalItems > DEFAULT_PAGE_SIZE && (
          <Pagination
            className={styles.MatchLogs_pagination}
            page={page}
            total={totalItems}
            perPage={DEFAULT_PAGE_SIZE}
            onPageChange={setPage}
          />
        )}
      </div>
      {isMatchActive ? (
        <LogsList className={styles.MatchLogs_logs} data={lastLogs} />
      ) : (
        <>
          {isLogsHistoryLoading && (
            <SpinnerCircular
              className={styles.MatchLogs_loader}
              size={32}
              color="#352626"
            />
          )}
          <LogsList
            className={clsx(styles.MatchLogs_logs, {
              [styles.loading]: isLogsHistoryLoading
            })}
            data={logsHistoryData ?? (EMPTY_LOGS as IGameplayLog[])}
          />
        </>
      )}
    </div>
  )
}
